import {
  CheckRow,
  DepthGradient,
  MetaGrid,
  MetaItem,
  Panel,
  Pill,
  QualState,
  ScreenHeader,
  StateBox,
  WeakestLinkBanner,
  checkStateToPill,
  recordStateToQual,
} from '@garnet/ui';
import { useAsync } from '../useAsync.js';
import type { PortalApi } from '../data/types.js';
import { visibleAtTier, withheldCount } from '../data/shape.js';

export function SharedRecord({ api }: { api: PortalApi }) {
  const { data, loading, error } = useAsync(() => api.getSharedRecord());

  if (loading) return <StateBox kind="loading" title="Loading your shared record" />;
  if (error || !data) return <StateBox kind="error" title="Could not load the record">{error}</StateBox>;

  const visible = visibleAtTier(data.checks, data.granted_tier);
  const withheld = withheldCount(data.checks, data.granted_tier);

  return (
    <div className="screen">
      <ScreenHeader
        eyebrow={`${data.granted_tier} tier · ${data.record_id}`}
        title="Your qualification record"
        actions={<QualState state={recordStateToQual(data.state)} />}
      />

      <Panel className="pad">
        <MetaGrid labelWidth={130}>
          <MetaItem label="record">{data.record_id}</MetaItem>
          <MetaItem label="supplier">{data.supplier}</MetaItem>
          <MetaItem label="granted tier">{data.granted_tier}</MetaItem>
          <MetaItem label="depth reached">{data.depth_reached}</MetaItem>
        </MetaGrid>
      </Panel>

      <DepthGradient reached={data.depth_reached} />

      <WeakestLinkBanner tone="flag">
        <strong>{data.weakest.check} · unit authenticity.</strong> {data.weakest.text}
      </WeakestLinkBanner>

      <Panel>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
            padding: '11px 14px',
            borderBottom: '1px solid var(--ink)',
          }}
        >
          Checks · disclosed at your tier
        </div>
        {visible.map((c) => (
          <CheckRow
            key={c.id}
            id={c.id}
            name={c.name}
            source={c.source ?? '—'}
            status={<Pill state={c.state}>{checkStateToPill(c.state).label}</Pill>}
          />
        ))}
      </Panel>

      {withheld > 0 && (
        <p style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', margin: 0 }}>
          {withheld} authority-tier {withheld === 1 ? 'field is' : 'fields are'} withheld at the restricted tier.
        </p>
      )}
    </div>
  );
}
